import { useState } from "react";
import { useEffect } from "react";

import AddIcon from "@mui/icons-material/Add";
import PreviewIcon from "@mui/icons-material/Preview";
import RemoveIcon from "@mui/icons-material/Remove";
import { Box } from "@mui/material";
import { Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import { v4 as uuid } from "uuid";

import constants from "../../assets/constants";
import TextInputDialog from "../dialogs/textInputDialog";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogTitle from "../ui/omniDialogTitle";
import OmniMenuItem from "../ui/omniMenuItem";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import OmniTextField from "../ui/omniTextField";

const BulkInviteDialog = props => {
  const {
    open,
    dialogTitle = "Edit Invitation",
    okMessage = "OK",
    cancelMessage = "Cancel",
    maxWidth = "sm",
    fullWidth = true,
  } = props;

  const [campaignTarget, setCampaignTarget] = useState("");
  const [campaignGroupSize, setCampaignGroupSize] = useState(1000001);

  const [onboardingHTML, setOnboardingHTML] = useState("");
  const [onboardingHTMLName, setOnboardingHTMLName] = useState("");

  useEffect(() => {
    try {
      if (onboardingHTML) {
        const splitPath = onboardingHTML.split("/");
        setOnboardingHTMLName(splitPath[splitPath.length - 1]);
      } else {
        setOnboardingHTMLName("");
      }
    } catch (error) {
      console.log(error);
    }
  }, [onboardingHTML]);

  const [showOnboardingHTML, setShowOnboardingHTML] = useState(false);

  const [onboardingAssessmentType, setOnboardingAssessmentType] = useState(
    constants.assessmentIDs.omniHealthHistory,
  );

  const onAcceptHandler = () => {
    let theReturn = {
      // A little off the top
      target: campaignTarget.trim(),
      groupSize: campaignGroupSize,
      onboardingHTML: onboardingHTML,
      onboardingAssessmentType: onboardingAssessmentType,
    };
    props.onAccept(theReturn);
  };

  const onCloseHandler = () => {
    props.onClose();
  };

  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onCloseHandler();
        }
      }}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>

      <DialogContent>
        <Typography variant="overline">Invitation Settings</Typography>
        <Grid container>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <OmniTextField
                label="Onboarding Assessment"
                select
                name={uuid()} //prevents AutoComplete Suggestions
                variant="outlined"
                id={"onboardingAssessmentType"}
                value={onboardingAssessmentType}
                onChange={event => {
                  setOnboardingAssessmentType(event.target.value);
                }}
                placeholder={"Assessment Type"}
                required
              >
                {constants.supportedAssessmentTypes.map(assType => (
                  <OmniMenuItem key={assType.value} value={assType.value}>
                    {assType.label}
                  </OmniMenuItem>
                ))}
              </OmniTextField>
            </Grid>

            <Grid item xs={8} sm={6}>
              <OmniTextField
                label="Campaign Name"
                type="textInput"
                name={uuid()} //prevents AutoComplete Suggestions
                variant="outlined"
                id={"target"}
                value={campaignTarget}
                error={campaignTarget.length === 0}
                onChange={event => {
                  setCampaignTarget(event.target.value);
                }}
                placeholder={"What is this For?"}
                required
              />
            </Grid>
          </Grid>
        </Grid>

        <Typography variant="overline">Campaign Segments</Typography>
        <Grid container>
          <Grid container spacing={1}>
            <Grid item xs={4} sm={4}>
              <OmniTextField
                label="Segment Size"
                type="textInput"
                name={uuid()} //prevents AutoComplete Suggestions
                variant="outlined"
                id={"target"}
                value={campaignGroupSize}
                onChange={event => {
                  setCampaignGroupSize(event.target.value);
                }}
                select
              >
                <OmniMenuItem key={1000001} value={1000001}>
                  {"Disabled"}
                </OmniMenuItem>

                <OmniMenuItem key={25} value={25}>
                  {"25 Invites"}
                </OmniMenuItem>
                <OmniMenuItem key={50} value={50}>
                  {"50 Invites"}
                </OmniMenuItem>
                <OmniMenuItem key={75} value={75}>
                  {"75 Invites"}
                </OmniMenuItem>
                <OmniMenuItem key={100} value={100}>
                  {"100 Invites"}
                </OmniMenuItem>
                <OmniMenuItem key={150} value={150}>
                  {"150 Invites"}
                </OmniMenuItem>
                <OmniMenuItem key={200} value={200}>
                  {"200 Invites"}
                </OmniMenuItem>
                <OmniMenuItem key={250} value={250}>
                  {"250 Invites"}
                </OmniMenuItem>
              </OmniTextField>
            </Grid>

            <Grid item xs={8} sm={8}>
              {campaignGroupSize < 1000000 && (
                <Typography
                  variant="buttonText"
                  sx={{ marginLeft: theme.spacing(1), lineHeight: "52px" }}
                  // sx={{ lineHeight: theme.spacing(7), fontWeight: "bold" }}
                >
                  {`Add a Segment Identifier to every ${campaignGroupSize} Invites.`}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={12}>
            <Typography variant="overline" sx={{ marginTop: theme.spacing(1) }}>
              Custom Onboarding HTML
            </Typography>
            <Box
              sx={{
                whiteSpace: "nowrap",
                marginBottom: theme.spacing(1),
              }}
            >
              <OmniResponsiveButton
                tooltipTitle="Set the Onboarding HTML"
                buttonText={onboardingHTML ? "remove" : "select"}
                icon={onboardingHTML ? <RemoveIcon /> : <AddIcon />}
                component="label"
                sx={{ marginLeft: theme.spacing(1) }}
                onClick={event => {
                  if (onboardingHTML) {
                    setOnboardingHTML("");
                  } else {
                    setShowOnboardingHTML(true);
                  }
                }}
              />

              <a href={`${onboardingHTML}`} target="_blank" rel="noopener noreferrer">
                <OmniResponsiveButton
                  tooltipTitle="Preview Onboarding HTML"
                  buttonText="Preview"
                  component="label"
                  icon={<PreviewIcon />}
                  disabled={!onboardingHTML}
                  sx={{ marginLeft: theme.spacing(1) }}
                />
              </a>

              {onboardingHTMLName && (
                <Typography
                  variant="buttonText"
                  sx={{ marginLeft: theme.spacing(1), fontWeight: "bold" }}
                >
                  {onboardingHTMLName}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container item justifyContent="flex-end" xs={6} spacing={1}>
          <Grid item>
            {cancelMessage && (
              <Button onClick={onCloseHandler} color="secondary" variant="contained">
                {cancelMessage}
              </Button>
            )}
          </Grid>
          <Grid item>
            <Button
              onClick={onAcceptHandler}
              color="secondary"
              variant="contained"
              disabled={campaignTarget.length === 0}
            >
              {okMessage}
            </Button>
          </Grid>
        </Grid>
      </DialogActions>

      {showOnboardingHTML && (
        <TextInputDialog
          dialogTitle="Object URL"
          dialogMessage={`Paste the file URL into this text box.\nThe optimal width and height is ${"~800 x 600"}`}
          inputLabel={`${"Onboarding HTML File"}`}
          open={showOnboardingHTML}
          onAccept={theText => {
            setOnboardingHTML(theText);
            setShowOnboardingHTML(false);
          }}
          onClose={() => {
            setShowOnboardingHTML(false);
          }}
        />
      )}
    </Dialog>
  );
};

export default BulkInviteDialog;
